import React from 'react';
import { Link, useParams } from 'react-router-dom';

import { ItemsProvider, useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import CustomRequestProvider from '@app/components/CustomRequestProvider/CustomRequestProvider';
import { ICON_LINK, TEXT_LINK } from '@app/components/Pages/Resources/FamilyResources/FRUtils';
import CustomAvatar from '@app/components/UI/CustomAvatar/CustomAvatar';
import HelmetProvider from '@app/components/UI/HelmetProvider/HelmetProvider';
import LoadMoreButton from '@app/components/UI/LoadMoreButton/LoadMoreButton';
import ModalVideo from '@app/components/UI/ModalVideo/ModalVideo';
import imagePlaceholder from '@app/images/image-placeholder.png';
import { ContentCategory } from '@app/objects/ContentCategory';
import { ContentEntity, ContentTopic, ContentType } from '@app/objects/ContentTopic';

const initBlogItemsCount = 10;

interface FRBlogProps {
	currentCategoryBlog?: ContentTopic;
	categoriesBlog?: Array<ContentCategory>;
	onSetCategory: (e: ContentCategory) => void;
	searchQuery: string | undefined;
}

interface BlogProps extends FRBlogProps {
	paramsId: string | undefined;
}

const Blog: React.FC<BlogProps> = ({
	paramsId,
	currentCategoryBlog,
	categoriesBlog,
	onSetCategory,
	searchQuery,
}) => {
	const [modalVisible, setModalVisible] = React.useState<boolean>(false);
	const [videoSrc, setVideoSrc] = React.useState<string>('');
	const [loadingSSR, setLoadingSSR] = React.useState<boolean>(true);

	const context = useItemsProviderContext<ContentEntity>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { items, loading },
		actions: { handleChange },
	} = context;

	React.useEffect(() => {
		setLoadingSSR(false);
	}, []);

	React.useEffect(() => {
		if (categoriesBlog) {
			categoriesBlog.forEach((item) => {
				if (item.url === paramsId) {
					onSetCategory(item);
					return false;
				}
			});
		}
	}, [currentCategoryBlog]);

	React.useEffect(() => {
		handleChange({
			text: searchQuery || '',
			offset: 0,
		});
	}, [searchQuery, paramsId]);

	return (
		<>
			<div className="blog-page__content">
				{!loading && items.map((item, index) => (
					<article key={index} className="article">
						<div className="article__info">
							<h6 className="article__caption">{item.title}</h6>
							<p className="article__text">{item.smallText}</p>
							<div className="article__addition">
								{item.contentType === ContentType.Video ? (
									<span
										onClick={() => {
											setModalVisible(true);
											setVideoSrc(item.url);
										}}
										className="article__more btn-text"
									>
										{TEXT_LINK[item.contentType]}
										{' '}
										<img className="arrow-icon" src={ICON_LINK[item.contentType]} alt="Arrow" />
									</span>
								) : item.contentType === ContentType.Article ? (
									<Link to={`/blog/${item.topic.url}/${item.url}`} className="article__more btn-text">
										{TEXT_LINK[item.contentType]}
										{' '}
										<img className="arrow-icon" src={ICON_LINK[item.contentType]} alt="Arrow" />
									</Link>
								) : (
									<a href={item.url} className="article__more btn-text" target="_blank" rel="noopener noreferrer">
										{TEXT_LINK[item.contentType]}
										{' '}
										<img className="arrow-icon" src={ICON_LINK[item.contentType]} alt="Arrow" />
									</a>
								)}

								<span className="article__name-date">{currentCategoryBlog?.name || item.topic?.name}</span>
							</div>
						</div>
						<div className="article__photo">
							<CustomAvatar src={item.avatar} errorAvatar={imagePlaceholder} maxWidth={300} maxHeight={300} />
						</div>
					</article>
				))}

				{!loadingSSR && !loading && !items.length && (
					<div className="faq-block__dummy">
						Woof! We were not able to find a match. Please try another search.
					</div>
				)}

				{(loadingSSR || loading) && <BounceLoader />}
				<div className="text-center">
					<LoadMoreButton
						className="btn btn-lg btn-secondary blog-page__load-more"
						visible={!loading}
					/>
				</div>
			</div>

			<ModalVideo modalVisible={modalVisible} setModalVisible={setModalVisible} videoSrc={videoSrc} />
		</>
	);
};

const FRBlog: React.FC<FRBlogProps> = ({
	currentCategoryBlog,
	categoriesBlog,
	onSetCategory,
	searchQuery,
}) => {
	const params = useParams<{ id: string | undefined }>();

	return (
		<HelmetProvider showLapOfLoveTitle={false}>
			<CustomRequestProvider version="v2">
				<ItemsProvider
					type={`contentTopics/blog/${params.id ? `${params.id}/` : ''}content`}
					loadRequest={`contentTopics/blog/${params.id ? `${params.id}/` : ''}content`}
					pagination={{ pageSize: initBlogItemsCount, total: 0 }}
				>
					<Blog
						currentCategoryBlog={currentCategoryBlog}
						paramsId={params.id}
						categoriesBlog={categoriesBlog}
						onSetCategory={onSetCategory}
						searchQuery={searchQuery}
					/>
				</ItemsProvider>
			</CustomRequestProvider>
		</HelmetProvider>
	);
};

export default FRBlog;
